import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Button, ErrorText, cx } from './ui';
import { XIcon } from './icons';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { removeAvatar, uploadAvatar, type Me } from '../queries/account';

/**
 * The profile picture, with a crop step. Pick a file, drag it around inside
 * a circle, zoom with the slider or the wheel, save. What goes up is the
 * square that circle sits in, rendered to 512px so the API never sees a
 * 12 MB phone photo.
 */

const CROP = 288; // on-screen crop window, px
const OUTPUT = 512;
const MAX_ZOOM = 4;

export function ProfilePicture({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null);

  const settle = (next: Me) => {
    track('Profile Updated', { field: 'avatar' });
    queryClient.setQueryData(['me'], next);
  };
  const picture = useMutation({ mutationFn: uploadAvatar, onSuccess: settle });
  const clearPicture = useMutation({ mutationFn: removeAvatar, onSuccess: settle });

  const initial = (me.name || me.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div>
      <div className="flex items-center gap-4">
        {me.avatarUrl ? (
          <img src={me.avatarUrl} alt="" referrerPolicy="no-referrer" className="h-16 w-16 rounded-full object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-800 text-h3 text-chalk">
            {initial}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/heic"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) setPending(file);
              event.target.value = '';
            }}
          />
          <Button size="sm" busy={picture.isPending} onClick={() => fileInput.current?.click()}>
            {me.avatarUrl ? 'Change picture' : 'Upload a picture'}
          </Button>
          {me.avatarUrl ? (
            <Button size="sm" busy={clearPicture.isPending} onClick={() => clearPicture.mutate()}>
              Remove
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-3 text-caption text-chalk-faint">Any image. You crop it to a circle before it saves.</p>
      {picture.isError ? (
        <div className="mt-2">
          <ErrorText>{errorMessage(picture.error)}</ErrorText>
        </div>
      ) : null}
      {clearPicture.isError ? (
        <div className="mt-2">
          <ErrorText>{errorMessage(clearPicture.error)}</ErrorText>
        </div>
      ) : null}

      {pending ? (
        <CropDialog
          file={pending}
          busy={picture.isPending}
          error={picture.isError ? errorMessage(picture.error) : null}
          onCancel={() => setPending(null)}
          onSave={(blob) => picture.mutate(blob, { onSuccess: () => setPending(null) })}
        />
      ) : null}
    </div>
  );
}

interface Placement {
  zoom: number; // multiplier on the "cover" scale
  x: number; // offset of the image centre from the crop centre, px on screen
  y: number;
}

function CropDialog({
  file,
  busy,
  error,
  onCancel,
  onSave,
}: {
  file: File;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (blob: Blob) => void;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [placement, setPlacement] = useState<Placement>({ zoom: 1, x: 0, y: 0 });
  const drag = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);
  const frame = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setLoadError('That file did not open as an image.');
    img.src = url;
    setPlacement({ zoom: 1, x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // "Cover" scale: the smaller image side fills the crop window at zoom 1.
  const cover = image ? CROP / Math.min(image.naturalWidth, image.naturalHeight) : 1;
  const scale = cover * placement.zoom;
  const drawnW = image ? image.naturalWidth * scale : 0;
  const drawnH = image ? image.naturalHeight * scale : 0;

  // The image may never leave a gap inside the circle.
  const clamp = (next: Placement): Placement => {
    if (!image) return next;
    const s = cover * next.zoom;
    const maxX = Math.max(0, (image.naturalWidth * s - CROP) / 2);
    const maxY = Math.max(0, (image.naturalHeight * s - CROP) / 2);
    return {
      zoom: next.zoom,
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  };

  const setZoom = (zoom: number) =>
    setPlacement((current) => clamp({ ...current, zoom: Math.min(MAX_ZOOM, Math.max(1, zoom)) }));

  // React's onWheel is passive, so the page would scroll under the dialog;
  // a native listener gets to say no to that.
  useEffect(() => {
    const node = frame.current;
    if (!node || !image) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setPlacement((current) => {
        const zoom = Math.min(MAX_ZOOM, Math.max(1, current.zoom * (event.deltaY < 0 ? 1.06 : 1 / 1.06)));
        const s = cover * zoom;
        const maxX = Math.max(0, (image.naturalWidth * s - CROP) / 2);
        const maxY = Math.max(0, (image.naturalHeight * s - CROP) / 2);
        return { zoom, x: Math.min(maxX, Math.max(-maxX, current.x)), y: Math.min(maxY, Math.max(-maxY, current.y)) };
      });
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [image, cover]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startY: event.clientY, originX: placement.x, originY: placement.y };
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setPlacement((current) =>
      clamp({ ...current, x: d.originX + (event.clientX - d.startX), y: d.originY + (event.clientY - d.startY) }),
    );
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const save = () => {
    if (!image) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Map the on-screen crop window onto the source image.
    const ratio = OUTPUT / CROP;
    const left = (CROP - drawnW) / 2 + placement.x;
    const top = (CROP - drawnH) / 2 + placement.y;
    ctx.fillStyle = '#0b0f14';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, left * ratio, top * ratio, drawnW * ratio, drawnH * ratio);
    canvas.toBlob(
      (blob) => {
        if (blob) onSave(blob);
      },
      'image/jpeg',
      0.9,
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="relative w-full max-w-sm rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk"
        >
          <XIcon size={16} />
        </button>
        <h2 id="crop-title" className="text-h4 text-chalk">
          Crop your picture
        </h2>
        <p className="mt-1 text-caption text-chalk-dim">Drag to move, scroll or slide to zoom.</p>

        <div className="mt-5 flex justify-center">
          <div
            ref={frame}
            className={cx(
              'relative select-none overflow-hidden rounded-md bg-slate-950',
              image ? 'cursor-grab active:cursor-grabbing' : '',
            )}
            style={{ width: CROP, height: CROP, touchAction: 'none' }}
            onPointerDown={image ? onPointerDown : undefined}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {image ? (
              <img
                src={image.src}
                alt=""
                draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{
                  width: drawnW,
                  height: drawnH,
                  left: (CROP - drawnW) / 2 + placement.x,
                  top: (CROP - drawnH) / 2 + placement.y,
                }}
              />
            ) : null}
            {/* The circle mask: everything outside it is dimmed, not hidden,
                so you can see what you are pushing out of frame. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-chalk/30"
              style={{ boxShadow: '0 0 0 999px rgb(var(--c-page) / 0.7)' }}
            />
          </div>
        </div>

        <label className="mt-5 flex items-center gap-3 text-caption text-chalk-dim">
          <span className="shrink-0">Zoom</span>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={placement.zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            disabled={!image}
            className="w-full accent-signal"
          />
        </label>

        {loadError || error ? (
          <div className="mt-3">
            <ErrorText>{loadError ?? error}</ErrorText>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} busy={busy} disabled={!image}>
            Save picture
          </Button>
        </div>
      </div>
    </div>
  );
}
